import { Typography } from 'antd';
import dayjs from 'dayjs';
import { memo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { OmniChannelSenderDto, OmniChannelMessageDto } from 'types';
import { stringToHslColor, TIME_FORMAT, transformUrlsToLinks } from 'utils';
import { MessageOptionsRef } from './MessageOptions';
import MessageUrl from './MessageUrl';
import { useOmniChatContext, useOmniHistoriesContext } from 'contexts';
import { EditIcon } from 'assets';

type MessageActionProps = {
  data: OmniChannelMessageDto;
  sender?: OmniChannelSenderDto;
  isGroup?: boolean;
  isFirstOfSender?: boolean;
  onForwardMessage?: (message: OmniChannelMessageDto) => void;
  isImageMessage?: boolean;
  className?: string;
  style?: React.CSSProperties;
};

const editorIcon = `<svg width="16" height="16" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M196.452 125.65L214.119 174.739C223.813 201.651 239.321 226.092 259.537 246.319C279.754 266.547 304.181 282.063 331.077 291.762L380.14 309.439C381.109 309.795 381.945 310.439 382.536 311.286C383.127 312.132 383.444 313.14 383.444 314.172C383.444 315.204 383.127 316.212 382.536 317.058C381.945 317.905 381.109 318.549 380.14 318.905L331.077 336.582C304.181 346.281 279.754 361.797 259.537 382.025C239.321 402.253 223.813 426.693 214.119 453.605L196.452 502.695C196.097 503.664 195.453 504.501 194.607 505.092C193.761 505.683 192.754 506 191.722 506C190.69 506 189.683 505.683 188.837 505.092C187.991 504.501 187.347 503.664 186.992 502.695L169.325 453.605C159.631 426.693 144.123 402.253 123.906 382.025C103.69 361.797 79.263 346.281 52.3663 336.582L3.30366 318.905C2.33487 318.549 1.49852 317.905 0.90768 317.058C0.316839 316.212 0 315.204 0 314.172C0 313.14 0.316839 312.132 0.90768 311.286C1.49852 310.439 2.33487 309.795 3.30366 309.439L52.3663 291.762C79.263 282.063 103.69 266.547 123.906 246.319C144.123 226.092 159.631 201.651 169.325 174.739L186.992 125.65C187.337 124.67 187.977 123.823 188.825 123.223C189.672 122.623 190.684 122.301 191.722 122.301C192.76 122.301 193.772 122.623 194.619 123.223C195.466 123.823 196.107 124.67 196.452 125.65ZM417.3 7.67992L426.254 32.533C431.167 46.1587 439.022 58.5332 449.259 68.7758C459.496 79.0184 471.864 86.8773 485.482 91.793L510.321 100.752C510.813 100.932 511.238 101.258 511.539 101.687C511.839 102.117 512 102.628 512 103.152C512 103.676 511.839 104.187 511.539 104.616C511.238 105.046 510.813 105.372 510.321 105.551L485.482 114.511C471.864 119.427 459.496 127.285 449.259 137.528C439.022 147.771 431.167 160.145 426.254 173.771L417.3 198.624C417.121 199.116 416.795 199.542 416.366 199.842C415.937 200.143 415.426 200.304 414.902 200.304C414.378 200.304 413.867 200.143 413.438 199.842C413.009 199.542 412.683 199.116 412.504 198.624L403.549 173.771C398.636 160.145 390.782 147.771 380.545 137.528C370.308 127.285 357.94 119.427 344.322 114.511L319.483 105.551C318.991 105.372 318.566 105.046 318.265 104.616C317.965 104.187 317.804 103.676 317.804 103.152C317.804 102.628 317.965 102.117 318.265 101.687C318.566 101.258 318.991 100.932 319.483 100.752L344.322 91.793C357.94 86.8773 370.308 79.0184 380.545 68.7758C390.782 58.5332 398.636 46.1587 403.549 32.533L412.504 7.67992C412.683 7.18755 413.009 6.76225 413.438 6.46173C413.867 6.1612 414.378 6 414.902 6C415.426 6 415.937 6.1612 416.366 6.46173C416.795 6.76225 417.121 7.18755 417.3 7.67992Z" fill="black"/>
</svg>`;

const MessageAction: React.FC<MessageActionProps> = ({
  data,
  sender,
  isFirstOfSender,
  isImageMessage,
  className,
  style
}) => {
  const { chatSession } = useOmniChatContext();
  const customerId = chatSession.chat?.customerId;
  const isCustomer = data.senderId === customerId;

  const content = `<span class=" mr-2 leading-4  inline-block translate-y-0.5">${editorIcon}</span>${data.content}`;
  const isUrls = !!data.metadata && !isImageMessage;
  const isHasSender = Boolean(sender && isFirstOfSender);

  return (
    <div className='relative'>
      <div
        className={twMerge(
          `relative min-w-[70px] whitespace-pre-wrap break-words rounded-lg border border-colorTextBase px-3 pb-8 pt-2 text-left text-sm`,
          !isCustomer ? 'bg-colorBgMyMessage2' : 'bg-colorBgCustomerMessage2',
          data.isHidden && 'opacity-80',
          data.isDeleted && 'opacity-60 brightness-75',
          className
        )}
        style={{
          maxWidth: 556,
          ...style
        }}
      >
        {!isImageMessage && isHasSender && (
          <Typography.Paragraph
            style={{
              color: stringToHslColor(sender!.senderName, 80, 35)
            }}
            className='mb-2 text-sm font-semibold'
          >
            {sender!.senderName}
          </Typography.Paragraph>
        )}
        <div dangerouslySetInnerHTML={{ __html: content }}></div>

        <Typography.Paragraph className='absolute bottom-2 right-3 mb-0 select-none text-xs' type='secondary'>
          {dayjs(data.createdAt).format(TIME_FORMAT)}
        </Typography.Paragraph>
      </div>
      {/* <div className={twMerge('absolute bottom-[3px] left-2')}>
        <MessageActions data={data} />
      </div> */}
    </div>
  );
};

export default memo(MessageAction);
